import { createBrowserHistory } from "history";
import React from "react";

import {
    AddonAttributes,
    CloudAttributes,
    DeviceAttributes,
    SiteAttributes,
    SNVRAttributes,
    UserAttributes,
    UserCloudAttributes,
    UserSitePermission
} from "app/models";
import { VpcOptions } from "app/theme";
import { getEmbeddedJson } from "app/utils";

export interface ExternalLinks {
    help: string | null;
    privacyPolicy: string | null;
}

export type PageLevel = "cloud" | "site" | "device" | "smartnvr";

interface StaticContext {
    environment: "production" | "integration" | "development";
    pageLevel: PageLevel;
    appVersion: string;
    csrfToken: string;
    googleMapsKey: string;
    externalLinks: ExternalLinks;
    user: UserAttributes;
    theme: VpcOptions;
    // Per-environment feature flags from the feature_flags table (BaseController::createContext).
    // Keyed by feature name, e.g. features.iot (ECCLOUD-9023). A missing key means disabled.
    features: { [name: string]: boolean };
}

export interface DynamicContext {
    cloud: CloudAttributes;
    smartNVR?: SNVRAttributes;
    userCloud: UserCloudAttributes;
    permissions: UserSitePermission[] | true;
    site?: SiteAttributes;
    enabledAddons?: string[];
    enabledConfigTabs?: string[];
    device?: DeviceAttributes;
    uploadsUrl: string;
    addon?: AddonAttributes;
    canManageVouchers: boolean;
}

export interface PartialContext extends Partial<DynamicContext>, StaticContext {
}

export interface CloudContext extends DynamicContext, StaticContext {
}

export interface SNVRContext extends PartialContext {
    snvr: SNVRAttributes;
}

export interface SiteContext extends PartialContext {
    site: SiteAttributes;
    enabledAddons: string[];
}

export interface AddonContext extends PartialContext {
    addon: AddonAttributes;
}

export interface DeviceContext extends PartialContext {
    device: DeviceAttributes;
}

export interface UrlParams {
    cloudId?: string;
    siteId?: string;
    deviceId?: string;
    addonId?: string;
    smartnvrId?: string;
    siteFilterId?: string;
    transactionId?: string;
}

export const staticContext: StaticContext = getEmbeddedJson("context");
const legacyGlobalVars: any = getEmbeddedJson("global");

export const pageContext = React.createContext(staticContext as PartialContext);
export const browserHistory = createBrowserHistory();

let urlParamsCache: UrlParams | undefined;

export function getUrlParams(): UrlParams {
    if (urlParamsCache) {
        return urlParamsCache;
    }

    // Cache URL params until next tick
    window.setTimeout(() => urlParamsCache = undefined, 0);

    const pathname = window.location.pathname;

    const matchCloud = pathname.match(/\/cloud\/(\d+)/);
    const matchSite = pathname.match(/\/site\/(\d+)/);
    const matchDevice = pathname.match(/\/device\/(\d+)/);
    const matchAddon = pathname.match(/\/addon\/(\d+)/);
    const matchSiteFilter = pathname.match(/\/site-filter\/(\d+)/);
    const matchSmartNVR = pathname.match(/\/smartnvr\/(\d+)/);
    const matchPurchase = pathname.match(/\/purchase\/([0-9A-Z]+)/);

    let cloudId, siteId, deviceId, addonId, siteFilterId, smartnvrId, transactionId;

    if (legacyGlobalVars) {
        cloudId = legacyGlobalVars.getCloudId;
    }

    if (matchCloud) {
        cloudId = matchCloud[1];
    }

    if (matchSite) {
        siteId = matchSite[1];
    }

    if (matchDevice) {
        deviceId = matchDevice[1];
    }

    if (matchAddon) {
        addonId = matchAddon[1];
    }

    if (matchSiteFilter) {
        siteFilterId = matchSiteFilter[1];
    }

    if (matchSmartNVR) {
        smartnvrId = matchSmartNVR[1];
    }

    if (!cloudId) {
        if (matchPurchase) {
            transactionId = matchPurchase[1];
        } else {
            throw new Error("Could not determine cloud ID by looking at global JSON or page URL");
        }
    }

    urlParamsCache = {
        cloudId,
        siteId,
        deviceId,
        addonId,
        siteFilterId,
        smartnvrId,
        transactionId
    };

    return urlParamsCache;
}

function setPageLevel(context: StaticContext) {
    const urlParams = getUrlParams();

    if (urlParams.deviceId) {
        context.pageLevel = "device";
    } else if (urlParams.siteId) {
        context.pageLevel = "site";
    } else if (urlParams.smartnvrId) {
        context.pageLevel = "smartnvr";
    } else {
        context.pageLevel = "cloud";
    }
}

setPageLevel(staticContext);

interface Props {
    dynamicContext?: DynamicContext;
    children: React.ReactNode;
}

export const ContextProvider = (props: Props) => {
    const Provider = pageContext.Provider;
    const context = Object.assign({}, staticContext, props.dynamicContext || {});

    setPageLevel(context);

    return (
        <Provider value={context}>
            {props.children}
        </Provider>
    );
};
